import { useApolloClient } from '@apollo/client'

import {
  FinanceFactSummariesDocument,
  FinanceFactSummariesQuery,
  FinancialWorkspaceValueObjectsCostTypeEnum,
  FinancialWorkspaceValueObjectsFinanceTypeEnum,
  FinancialWorkspaceValueObjectsInsightTableTypeEnum,
  InsightTableItemsDocument,
  InsightTableItemsQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import {
  InsightTableGroupBy,
  InsightTableType,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'
import { usePermissionsData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/PermissionsProvider'
import { useSourcesVisibility } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/useSourcesVisibility'
import { getFinanceTypes } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/lib/sources'

import { resolveItemsPerPage } from 'modules/finance/components/FinancialWorkspace/components/Table/const'
import {
  mapFinanceFactSummariesData,
  mapInsightTableItemsData,
} from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/mapQueryData'
import {
  shouldShowExpanderForFinanceSummaries,
  shouldShowExpanderForInsightTable,
} from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/expanderHelpers'
import { useComparisonData } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/useComparisonData'
import { useFetchChildrenForFinanceFactSummaries } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/useFetchChildren'

// Cost type for the query
// - EmployeeCosts uses 'labor_cost' to show labor cost calculations
// - Payroll and Compensation use 'normal' for standard values
const COST_TYPE_LABOR: FinancialWorkspaceValueObjectsCostTypeEnum = 'labor_cost'
const COST_TYPE_NORMAL: FinancialWorkspaceValueObjectsCostTypeEnum = 'normal'

interface UseDataCollectionSourceOptions {
  type: InsightTableType
  groupBy: InsightTableGroupBy
  search?: string
}

/**
 * Main data source hook for the DataCollectionTable
 *
 * Routes between two different query strategies:
 * - Payroll: Uses InsightTableItemsDocument
 * - EmployeeCosts/Compensation: Uses FinanceFactSummariesDocument
 *
 * Supports lazy loading of children for EmployeeCosts when rows are expanded.
 */
export const useDataCollectionSource = ({
  type,
  groupBy,
  search,
}: UseDataCollectionSourceOptions) => {
  const client = useApolloClient()
  const { formattedState } = useFinancialWorkspace()
  const {
    permissions: { canSeeCompensations },
  } = usePermissionsData()

  const visibility = useSourcesVisibility()
  const financeTypes = getFinanceTypes(visibility)

  const itemsPerPage = resolveItemsPerPage(type, groupBy)

  const useFinanceFactSummaries =
    type === InsightTableType.EmployeeCosts ||
    (type === InsightTableType.Compensation && canSeeCompensations)

  const compensationsFinanceTypes: FinancialWorkspaceValueObjectsFinanceTypeEnum[] = [
    'compensations',
  ]
  const effectiveFinanceTypes: FinancialWorkspaceValueObjectsFinanceTypeEnum[] =
    type === InsightTableType.EmployeeCosts
      ? financeTypes
      : type === InsightTableType.Compensation
        ? compensationsFinanceTypes
        : []

  const effectiveCostType =
    type === InsightTableType.Compensation || type === InsightTableType.Payroll
      ? COST_TYPE_NORMAL
      : COST_TYPE_LABOR

  // Serialize filter arrays for stable dependency tracking
  const legalEntityIdsKey = JSON.stringify(formattedState.legalEntityIds ?? [])
  const locationIdsKey = JSON.stringify(formattedState.locationIds ?? [])
  const costCenterIdsKey = JSON.stringify(formattedState.costCenterIds ?? [])
  const employeeIdsKey = JSON.stringify(formattedState.employeeIds ?? [])
  const conceptIdsKey = JSON.stringify(formattedState.conceptIds ?? [])
  const financeTypesKey = JSON.stringify(effectiveFinanceTypes)

  const formattedStartDate = formattedState.formattedStartDate
  const formattedEndDate = formattedState.formattedEndDate
  const formattedCompareToStartDate = formattedState.formattedCompareToStartDate
  const formattedCompareToEndDate = formattedState.formattedCompareToEndDate

  const hasComparisonDate = !!(formattedCompareToStartDate && formattedCompareToEndDate)

  const { comparisonDataRef, isLoadingComparison } = useComparisonData({
    client,
    useFinanceFactSummaries,
    groupBy,
    search,
    effectiveCostType,
    formattedCompareToStartDate,
    formattedCompareToEndDate,
    hasComparisonDate,
    legalEntityIdsKey,
    locationIdsKey,
    costCenterIdsKey,
    employeeIdsKey,
    conceptIdsKey,
    financeTypesKey,
  })

  const fetchChildrenForFinanceFactSummaries = useFetchChildrenForFinanceFactSummaries({
    client,
    groupBy,
    search,
    formattedStartDate,
    formattedEndDate,
    effectiveCostType,
    legalEntityIdsKey,
    locationIdsKey,
    costCenterIdsKey,
    employeeIdsKey,
    financeTypesKey,
  })

  // Use FinanceFactSummaries for Employee Costs and Compensations
  const financeFactSummariesSource = useGraphqlDataCollectionSource(
    {
      document: FinanceFactSummariesDocument,
      fetchPolicy: 'network-only',
      paginationType: 'infinite-scroll' as const,
      perPage: itemsPerPage,

      search: { enabled: false },

      mapData: (data: FinanceFactSummariesQuery) =>
        mapFinanceFactSummariesData(data, groupBy, comparisonDataRef.current),

      mapVariables: () => ({
        formattedStartDate: formattedState.formattedStartDate,
        formattedEndDate: formattedState.formattedEndDate,
        groupBy: groupBy,
        legalEntityIds: formattedState.legalEntityIds ?? [],
        locationIds: formattedState.locationIds ?? [],
        costCenterIds: formattedState.costCenterIds ?? [],
        employeeIds: formattedState.employeeIds ?? [],
        conceptIds: formattedState.conceptIds ?? [],
        search: search ?? undefined,
        financeTypes: effectiveFinanceTypes,
        customCategoryIds: [],
        managerIds: [],
        excludeChildrenData: groupBy === InsightTableGroupBy.Total,
        costType: effectiveCostType,
        itemsPerPage,
      }),

      mapPagination: (data: FinanceFactSummariesQuery) => {
        const connection = data.financialWorkspace.financeFactSummariesConnection
        return {
          total: connection.totalCount ?? 0,
          cursor: connection.pageInfo.endCursor ?? '0',
          hasNextPage: connection.pageInfo.hasNextPage ?? false,
        }
      },

      itemsWithChildren: (item: FinancialWorkspaceRecord) =>
        shouldShowExpanderForFinanceSummaries(groupBy, item),

      fetchChildren: async ({ item }: { item: FinancialWorkspaceRecord }) => {
        const children = await fetchChildrenForFinanceFactSummaries(item)
        return {
          records: children,
          type: 'detailed' as const,
        }
      },
    },
    [
      formattedState.formattedStartDate,
      formattedState.formattedEndDate,
      legalEntityIdsKey,
      locationIdsKey,
      costCenterIdsKey,
      employeeIdsKey,
      conceptIdsKey,
      groupBy,
      search,
      itemsPerPage,
      financeTypesKey,
      fetchChildrenForFinanceFactSummaries,
      isLoadingComparison,
    ]
  )

  // Use InsightTableItems for Payroll
  const insightTableType: FinancialWorkspaceValueObjectsInsightTableTypeEnum = 'payroll'

  const insightTableItemsSource = useGraphqlDataCollectionSource(
    {
      document: InsightTableItemsDocument,
      fetchPolicy: 'network-only',
      paginationType: 'infinite-scroll' as const,
      perPage: itemsPerPage,

      search: { enabled: false },

      mapData: (data: InsightTableItemsQuery) =>
        mapInsightTableItemsData(data, groupBy, comparisonDataRef.current),

      mapVariables: () => ({
        resourceFilter: {
          formattedStartDate: formattedState.formattedStartDate,
          formattedEndDate: formattedState.formattedEndDate,
          groupBy: groupBy,
          legalEntityIds: formattedState.legalEntityIds ?? [],
          locationIds: formattedState.locationIds ?? [],
          costCenterIds: formattedState.costCenterIds ?? [],
          employeeIds: formattedState.employeeIds ?? [],
          conceptIds: formattedState.conceptIds ?? [],
          search: search ?? undefined,
          insightTableType,
        },
        itemsPerPage,
      }),

      mapPagination: (data: InsightTableItemsQuery) => {
        const connection = data.financialWorkspace.insightTableItemsConnection
        return {
          total: connection.totalCount ?? 0,
          cursor: connection.pageInfo.endCursor ?? '0',
          hasNextPage: connection.pageInfo.hasNextPage ?? false,
        }
      },

      // For Payroll, children are always pre-loaded in the response
      itemsWithChildren: shouldShowExpanderForInsightTable,

      fetchChildren: ({ item }: { item: FinancialWorkspaceRecord }) => {
        // Children are pre-loaded for InsightTableItems
        return Promise.resolve({
          records: item.children ?? [],
          type: 'detailed' as const,
        })
      },
    },
    [
      formattedState.formattedStartDate,
      formattedState.formattedEndDate,
      legalEntityIdsKey,
      locationIdsKey,
      costCenterIdsKey,
      employeeIdsKey,
      conceptIdsKey,
      groupBy,
      search,
      itemsPerPage,
      isLoadingComparison,
    ]
  )

  return useFinanceFactSummaries ? financeFactSummariesSource : insightTableItemsSource
}
